/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2020-2022, Bulb Inc. For more information, please email contact@hellobulb.com.
 *          _
 *  _            | | _
 * | |         _ | || |
 * | |__   _  | || || |__
 * | '_ \ | | | || || '_ \
 * | (_) || |_| || || (_) |
 *  \___/  \___/ |_| \___/
 *
*/

import { Inject, Injectable } from '@angular/core';
import { merge } from 'lodash-es';
// Uppy's packages:
import Uppy from '@uppy/core';
import AwsS3 from '@uppy/aws-s3';
// Our services :
import { BehaviorSubject } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { UppyDashboardService } from 'src/app/asset-uploads/uppy-dashboard.service';
import { BULB_BUSY_COVER_SERVICE } from '../app/angularjs.providers';
import { BulbCKEStateService } from '../ckeditor/editor/services/bulb-cke-state.service';
import { ErrorService } from './error.service';
import { generateObjectId } from '../app/exports';

/**
 * Interface needed for Uppy.
 */
interface BulbFile {
  name : string;
}

/**
 * Angular service that uploads a file *directly* to S3 using the HTTPS Form POST method.
 */
@Injectable()
export class UploadAssetService {
  /**
   * Uppy Uploader instance for handling uploads.
   */
  public uppy : any;

  /**
   * The current upload state the user initiated.
   */
  private uploadState : string;

  /**
   * Indicating whether the user is uploading this from the assetlibrary or not.
   */
  private fromWithinAssetLibrary : boolean = true;

  /**
   * Subject which will be used to indicate upload action is in process.
   */
  uploadSubject : BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  /**
   * Default Options for the Uppy uploader instance.
   *
   * @see <a href="https://uppy.io/docs/uppy/#Options">Uppy Configurable options</a>
   */
  private defaultUppyOptions : any = {
    autoProceed: true,
    debug: true,
    restrictions: {
      // 10 GB
      maxFileSize: 10000000000,
    },
    onBeforeUpload: this.onBeforeUppyUpload.bind(this),
  };

  /**
   * Unique File id generated by Uppy when a file has been added to the internal state.
   */
  private fileId : String;

  /**
   * File array containing all the files the user has selected to upload.
   * FUTURE: use this property when we have multiple file upload enabled. Disabled for now.
   */
  private fileList : Array<String> = [];

  ckeStatus: any;

  BulbInsertBlockExtensionFactory: any;

  stopBusyIndicatorCallback = null;

  /**
   * Default Constructor.
   *
   * @param errorService Our own Error Service to handle throwing client errors.
   * @param BulbAssetLibraryService Our Asset Library Service.
   * @param BulbBusyCoverService Our Busy Cover Service.
   * @param BulbPageService Our page Service.
   * @param BulbModalService Our modal service.
   * @param bulbCKEStateService Our CKE state service.
   */
  constructor(
    private errorService : ErrorService,
    @Inject('BulbAssetLibraryService') private BulbAssetLibraryService,
    @Inject(BULB_BUSY_COVER_SERVICE) private BulbBusyCoverService,
    @Inject('BulbPageService') private BulbPageService,
    @Inject('BulbModalService') private BulbModalService,
    private bulbCKEStateService: BulbCKEStateService,
    private uppyDashboardService: UppyDashboardService,
    private http : HttpClient,
  ) {}

  /**
   * Method to conditionally run insert block extension factory.
   */
  getInsertBlockExtensionFactory() : void {
    if (this.ckeStatus === 'UPGRADED') {
      this.BulbInsertBlockExtensionFactory = window.bulbVars.BulbInsertBlockExtensionFactory;
    } else {
      this.BulbInsertBlockExtensionFactory = window.bulbVars.BulbMediumInsertBlockExtensionFactory;
    }
  }

  /**
   * Update CKE status flag to check for cke page.
   */
  updatePageCKEStatus() : void {
    if (this.BulbPageService.page && this.BulbPageService.page.unit) {
      this.ckeStatus = this.BulbPageService.page.unit.ckeStatus;
    }
  }

  /**
   * Upload File.
   *
   * @param $target Hidden input file input field to initiate the file selection upload.
   * @param allowedTypes Allowed file extension type.
   * @param fromWithinAssetLibrary Indicating whether the user is uploading this from the assetlibrary or not.
   * @param uploadState The current upload workflow state the user initiated.
   */
  uploadFile(
    $target : JQuery<HTMLElement>,
    allowedTypes : string,
    fromWithinAssetLibrary : boolean,
    uploadState : string,
  ) : void {

    this.updatePageCKEStatus();
    this.uploadState = uploadState;
    this.fromWithinAssetLibrary = fromWithinAssetLibrary;

    // Open up the browser file select modal:
    $target.trigger('click');

    // References to our AngularJS Controller / Services / Directives:
    const { AssetContainerController } = window.bulbVars;
    this.getInsertBlockExtensionFactory();
    const { BulbImageCarouselService } = window.bulbVars;
    const { ImageCarouselBlockDirective } = window.bulbVars;

    // Finalized Uppy configuration options :
    const finalUppyConfigurationOptions : any = this.generateUppyUploadOptions(allowedTypes);

    // Initialize Uppy uploader instance:
    this.uppy = new Uppy(finalUppyConfigurationOptions);
    this.initializeUppyEvents(
      AssetContainerController,
      this.BulbInsertBlockExtensionFactory,
      BulbImageCarouselService,
      ImageCarouselBlockDirective,
      $target,
    );
    this.initializeUppyAwsS3();

    $target.on('change', (event : any) => {
      const files = Array.from(event.target.files);
      files.forEach((file : File) => {
        try {
          const fileObject : any = {
            source: 'file input',
            name: file.name,
            type: file.type,
            data: file, // file blob
          };

          // Adding the selected file to Uppy's internal state.
          this.fileId = this.uppy.addFile(fileObject);
          this.fileList.push(this.fileId);
        } catch (error) {
          this.errorService.handleException(error);
        }
      });
    });
  }

  /**
   * Method to cancel current upload.
   *
   * Removing a file that is already being uploaded cancels that upload.
   */
  cancelUpload() : void {
    this.uppy.removeFile(this.fileId);
  }

  /**
   * Upload File from the drag and drop workflow.
   *
   * @param file Dropped file for upload.
   * @param allowedTypes Allowed types for dropped asset.
   * @param fromWithinAssetLibrary Indicating whether the user is uploading this from the assetlibrary or not.
   * @param uploadState The current upload workflow state the user initiated.
   */
  async uploadFileWithoutBrowser(
    file : File,
    allowedTypes : string,
    fromWithinAssetLibrary : boolean,
    uploadState : string,
  ) : Promise<void> {
    this.updatePageCKEStatus();
    // Set the correct upload state:
    this.uploadState = uploadState || 'draganddrop';
    this.fromWithinAssetLibrary = fromWithinAssetLibrary;

    // References to our AngularJS Controller / Services / Directives:
    this.getInsertBlockExtensionFactory();
    this.BulbInsertBlockExtensionFactory.assetSuccessHandler('image');
    const options:any = {
      allowedFileTypes: ['image/*'],
      multiSelect: false,
      uploadState: 'page',
    };
    this.uppy = await this.uppyDashboardService.initializeUppyForAutoProceed(options, true, file);
  }

  /**
   * Upload File from the url workflow.
   *
   * @param url Url for upload.
   * @param allowedTypes Allowed types for dropped asset.
   * @param fromWithinAssetLibrary Indicating whether the user is uploading this from the assetlibrary or not.
   * @param uploadState The current upload workflow state the user initiated.
   */
  async uploadFileWithUrl(
    url : string,
    allowedTypes : string,
    fromWithinAssetLibrary : boolean,
    uploadState : string,
  ) {
    // Add the file to the queue:
    try {
      const response = await fetch(url, {
        mode: 'cors',
        headers: {
          'Access-Control-Allow-Origin': '*',
        },
      });
      const data = await response.blob();
      const fileExtension = data.type?.split('/')[1] || 'jpeg';
      const file = new File([data], `${generateObjectId()}.${fileExtension}`, {
        type: data.type || 'image/jpeg',
      });
      this.uploadFileWithoutBrowser(file, allowedTypes, fromWithinAssetLibrary, uploadState);
    } catch (error) {
      this.errorService.handleException(error);
    }
  }

  /**
   * Initialize the Uppy AWS S3 Plugin for uploading directly to an S3 bucket.
   *
   *
   * @see <a href="https://uppy.io/docs/aws-s3/">Uppy AWS S3</a>
   * @private
   */
  initializeUppyAwsS3() : void {
    const { http } = this;
    // Using the Uppy AwsS3 Plugin to send files to the S3 Destination.
    this.uppy.use(AwsS3, {
      timeout: 0,
      getUploadParameters(file) {
        const postParams: any = {
          assetKey: file.assetKey,
          fileName: file.name,
          contentType: file.type,
        };

        return http.post('s3Policy', postParams).toPromise().then((s3Params: any) => ({
          method: s3Params.method,
          url: s3Params.url,
          fields: s3Params.fields,
        }));
      },
      getResponseData(responseText /* , response */) {
        return {
          url: responseText.match(/<Location>(.*?)<\/Location>/)[1],
        };
      },
    });
  }

  /**
   * Initialize the Uppy events that get fired. Key in modifying the upload process to integrate with content block
   * generation and what not.
   *
   * @see <a href="https://uppy.io/docs/uppy/#Events">Uppy Events</a>
   * @see <a href="https://uppy.io/docs/uppy/#upload">Uppy Upload Event</a>
   * @see <a href="https://uppy.io/docs/uppy/#upload-progress">Uppy Upload Progress Event</a>
   * @see <a href="https://uppy.io/docs/uppy/#upload-success">Uppy Upload Success Event</a>
   * @see <a href="https://uppy.io/docs/uppy/#complete">Uppy Upload Complete Event</a>
   *
   * @param AssetContainerController Our AngularJS AssetContainerController.
   * @param BulbInsertBlockExtensionFactory Our AngularJS Factory insert block extension.
   * @param BulbImageCarouselService Our Image Carousel Block service that handles carousel addition of images.
   * @param ImageCarouselBlockDirective Our Image Carousel Block directive that handles carousel addition of images
   * for medium editor.
   * @param $target Optional JQuery file input target.
   *
   * @private
   */
  private initializeUppyEvents(
    AssetContainerController : any,
    BulbInsertBlockExtensionFactory: any,
    BulbImageCarouselService : any,
    ImageCarouselBlockDirective : any,
    $target? : JQuery<HTMLElement>,
  ) : void {
    /**
     * Fired when upload starts.
     *
     * @see <a href="https://uppy.io/docs/uppy/#upload">Uppy Upload Event</a>
     */
    this.uppy.on('upload', (data) => {
      if (this.ckeStatus === 'UPGRADED'
          && (this.uploadState !== 'pageImageCarousel' && this.uploadState !== 'coverimage')) {
        this.uploadSubject = new BehaviorSubject<boolean>(true);
        this.bulbCKEStateService.handleSaveOperation.next(false);
        // TO DO : Remove this indicator with new uppy implementation
        // as it will block screen until asset uploads on server.
        this.stopBusyIndicatorCallback = this.BulbBusyCoverService.showBusy();
        this.bulbCKEStateService.stopBusyIndicator.next(this.stopBusyIndicatorCallback);
      } else if (this.ckeStatus === 'UPGRADED' && this.uploadState === 'pageImageCarousel') {
        this.bulbCKEStateService.handleSaveOperation.next(false);
      }

      // Grab file from Uppy's internal state :
      const file = this.uppy.getFile(data.fileIDs);
      let folderId = null;

      if (AssetContainerController) {
        folderId = AssetContainerController.currentFolderId;
      }

      // createAsset is async. Store the promise, so we can make sure this finishes before we finish processing.
      file.createPromise = this.createAsset(file.type, file, folderId)
        .then(() => {
          if (this.ckeStatus === 'UPGRADED' && this.uploadState === 'pageImageCarousel') {
            this.BulbAssetLibraryService.dialog.dismissAllModals();
            /* Show upload progress only while adding the image carousel from media options
             * and uploading asset from Desktop.
             */
            if (this.bulbCKEStateService.getDesktopUploadSelectionMode()
              && this.bulbCKEStateService.getImageCarouselCreation()) {
              this.stopBusyIndicatorCallback = this.BulbBusyCoverService.showBusy();
              this.bulbCKEStateService.stopBusyIndicator.next(this.stopBusyIndicatorCallback);
              // this.showUploadProgress(file.data);
            }
            return;
          }
          // Add to correct state -- i.e. AssetLibrary or Page Creation :
          switch (this.uploadState) {
            case 'assetlibrary':
            case 'draganddrop':
            case 'paste':
            case 'page':
              if (!this.fromWithinAssetLibrary) {
                BulbInsertBlockExtensionFactory.addFileHandler(null, file.data.entry);
                BulbInsertBlockExtensionFactory.submitFileHandler(null, file.data);
              } else {
                AssetContainerController.uploadFileAdd();
              }
              break;
            case 'pageImageCarousel':
              ImageCarouselBlockDirective.submitFileHandler(null, file.data);
              break;
            default:
              // Log the Error to Sentry.Io
              this.errorService.handleException(`Error uploading asset. Unkown upload state : ${
                this.uploadState}`);
              break;
          }
        });
    });

    /**
     * Fired each time file upload progress is available:
     *
     * @see <a href="https://uppy.io/docs/uppy/#upload-progress">Uppy Upload Progress Event</a>
     */
    this.uppy.on('upload-progress', (file, progress) => {
      const canonicalProgress = parseInt(((progress.bytesUploaded / progress.bytesTotal) * 100).toString(), 10);
      switch (this.uploadState) {
        case 'avatar':
        case 'coverimage':
          // Have nothing to handle in this case.
          break;
        case 'assetlibrary':
        case 'draganddrop':
        case 'paste':
        case 'page':
        case 'pageImageCarousel':
          // It's possible that method will be called before got response from create image api.
          // And in this case file.data.entry will be undefined.
          if (file.data.entry) {
            if (!this.fromWithinAssetLibrary) {
              BulbInsertBlockExtensionFactory.progressFileHandler(null, file.data, canonicalProgress);
            } else {
              AssetContainerController.uploadFileProgress(null, file.data, canonicalProgress);
            }
          }
          break;
        default:
          // Log the Error to Sentry.Io
          this.errorService.handleException(`Error uploading asset. Unknown upload state : ${
            this.uploadState}`);
          break;
      }
    });

    /**
     * Fired each time a single upload is completed.
     *
     * @see <a href="https://uppy.io/docs/uppy/#upload-success">Uppy Upload Success Event</a>
     */
    this.uppy.on('upload-success', (file /* , response */) => {

      if (!this.uploadSubject.value) {
        this.uploadSuccessHandler(
          file,
          BulbInsertBlockExtensionFactory,
          AssetContainerController,
          BulbImageCarouselService,
          ImageCarouselBlockDirective,
        );
      } else {
        this.uploadSubject.subscribe(() => {
          if (!this.uploadSubject.value) {
            this.uploadSuccessHandler(
              file,
              BulbInsertBlockExtensionFactory,
              AssetContainerController,
              BulbImageCarouselService,
              ImageCarouselBlockDirective,
            );
            this.uploadSubject.complete();
          }
        });
      }
    });

    /**
     * Fired when all uploads are complete.
     *
     * @see <a href="https://uppy.io/docs/uppy/#complete">Uppy Upload Complete Event</a>
     */
    this.uppy.on('complete', () => {
      // remove file upload JQuery element from body of the DOM if passed:
      if ($target) {
        $target.remove();
      }
    });
    this.handleError();
  }

  handleError(): void {
    /**
     * Fired each time file upload error occurs:
     *
     * @see <a href="https://uppy.io/docs/uppy/#upload-error">Uppy Upload Error Event</a>
     */
    this.uppy.on('upload-error', (file, error, response) => {
      // Log the error to Sentry.Io
      this.errorService.handleException(`Error uploading asset ${file.id}: ${error}: ${response}`);
      this.uppy.close();
    });

    /**
     * Fired when there is a failure to upload/encode the entire upload:
     *
     * @see <a href="https://uppy.io/docs/uppy/#error">Uppy Upload/Encode Failure Event</a>
     */
    this.uppy.on('error', (error) => {
      // Log the error to Sentry.
      // error.isNetworkError = file upload could have failed due to firewall or ISP issues
      this.errorService.handleException(`Error uploading/encoding assets - isNetworkError= ${
        error.isNetworkError}: ${error.stack}`);
      this.uppy.close();
    });
  }

  /**
   * Show upload progress while adding the image carousel.
   * @param data Image Carousel uploaded data.
   * @private
   */
  private showUploadProgress(data: Blob | File) {
    const uploadStartedEvent = new CustomEvent('assetLibraryUploadStarted', {
      detail: {
        uploadData: data,
      },
    });
    this.bulbCKEStateService.assetLibraryUploadEvent.next(uploadStartedEvent);
  }

  /**
   * Handle the process on upload assets successfully to the server.
   *
   * @param file This is the asset which has to be uploaded.
   * @param AssetContainerController Our AngularJS AssetContainerController.
   * @param BulbInsertBlockExtensionFactory Our AngularJS Factory insert block extension.
   * @param BulbImageCarouselService Our Image Carousel Block service that handles carousel addition of images.
   * @param ImageCarouselBlockDirective Our Image Carousel Block directive that handles carousel addition of images
   * for medium editor.
   *
   * @private
   */
  private uploadSuccessHandler(
    file,
    BulbInsertBlockExtensionFactory,
    AssetContainerController,
    BulbImageCarouselService,
    ImageCarouselBlockDirective,
  ) {
    // Kick of processing image on the server side now:
    const postParams = {
      assetKey: file.assetKey,
      fileName: file.name,
    };

    // Make sure the 'create' on bulb server is finished before finishing the upload processing
    file.createPromise.then(() => {
      const hideBusy = this.BulbBusyCoverService.showBusy();
      this.http.post(`assetlibrary/${file.data.entry.id}/process`, postParams)
        .subscribe(() => {
          switch (this.uploadState) {
            case 'assetlibrary':
            case 'draganddrop':
            case 'paste':
            case 'page':
              if (!this.fromWithinAssetLibrary) {
                BulbInsertBlockExtensionFactory.doneFileHandler(null, file.data);
              } else {
                AssetContainerController.uploadFileDone(null, file.data);
              }
              break;
            case 'pageImageCarousel':
              if (this.ckeStatus === 'UPGRADED') {
                BulbImageCarouselService.doneFileHandler('draganddrop', file.data);
              } else {
                ImageCarouselBlockDirective.doneFileHandler(null, file.data);
              }
              break;
            default:
              // Log the Error to Sentry.Io
              this.errorService.handleException(`Error uploading asset. Unknown upload state : ${
                this.uploadState}`);
              break;
          }
          hideBusy();
        }, (error) => {
          switch (this.uploadState) {
            case 'assetlibrary':
            case 'draganddrop':
            case 'paste':
            case 'page':
              if (!this.fromWithinAssetLibrary) {
                BulbInsertBlockExtensionFactory.errorFileHandler(file.data);
              }
              break;

            case 'pageImageCarousel':
              if (this.ckeStatus === 'UPGRADED') {
                BulbImageCarouselService.errorFileHandler(file.data);
              } else {
                ImageCarouselBlockDirective.errorFileHandler(file.data);
              }
              break;

            default:
              // Log the Error to Sentry.Io
              this.errorService.handleException(`Error uploading asset. Unknown upload state : ${
                this.uploadState}`);
              break;
          }

          // Make sure we remove all the busy indicators:
          hideBusy();
          const stopBusyIndicator = this.bulbCKEStateService.stopBusyIndicator.value;
          if (stopBusyIndicator) {
            stopBusyIndicator();
            this.bulbCKEStateService.stopBusyIndicator.next(null);
          }

          // Show the correct error modal
          if (error.status === 400) {
            if (error.data.developerMessage.includes('TIMEOUT')) {
              // The devMessage only shows TIMEOUT if our image processing couldn't process or finish, so show
              // an image specific error message:
              this.showErrorModal('upload_cover_invalid_size_or_type');
            } else {
              this.showErrorModal('upload_error_invalid_type');
            }
          }
        });
    });
  }

  /**
   * @desc Show error modal.
   *
   * @param modalBodyKey locale code which need to be translated.
   */
  private showErrorModal = function showErrorModal(modalBodyKey) {
    this.BulbModalService.show({
      fontAwesomeCode: 'fas fa-exclamation-triangle',
      titleCode: 'upload_failed',
      bodyCode: modalBodyKey,
      windowClass: 'bulb-aero-modal-body bulb-aero-modal-buttons bulb-aero-modal-buttons-secondary-dismissal',
      showCancelButton: false,
    });
  };

  /**
   * A function run before an upload begins. Is called with files object with all the files that are already in Uppy.
   *
   * We are going to start integrating our ( current ) upload process with Uppy here.
   * @param files
   */
  // eslint-disable-next-line class-methods-use-this
  private onBeforeUppyUpload(files) : any {
    // Careful to return a new object, not mutating the original `files`
    const updatedFiles = {};
    const key : String = generateObjectId();
    Object.keys(files).forEach((fileID) => {
      updatedFiles[fileID] = {
        ...files[fileID],
        assetKey: `${key}.${files[fileID].name.split('.').pop()}`,
      };
    });
    return updatedFiles;
  }

  /**
   * Private helper method to handle the creation of the asset.
   *
   * @param assetType type of asset to be created
   * @param file uploaded
   * @param folderId id of the assetlibrary folder in which asset is to be created, defaults to null if undefined
   * @private
   */
  private createAsset(assetType : string, file : File, folderId: string = null) : Promise< any > {
    const { BulbAssetLibraryService } = this;
    const { BulbBusyCoverService } = this;

    // Needed for compliance:
    const { data } = file;
    data.originalFiles = [];
    data.files = [];

    const entry = {
      name: file.name,
      credit: '',
      fileSize: file.size,
    };

    // Grab name of uploading asset to display on the uploading bar:
    const bulbFile : BulbFile = {
      name: file.name,
    };
    file.data.originalFiles.push(bulbFile);
    file.data.files.push(bulbFile);

    if (assetType.includes('image')) {
      const reader = new FileReader();
      reader.onload = function onload(event) {
        data.target = event.target;
      };
      reader.readAsDataURL(file.data);
      return BulbBusyCoverService.showBusyDuring(
        BulbAssetLibraryService.api.createImage(folderId, entry).then((res) => {
          data.entry = res.entry;
        }),
      );
    } if (assetType.includes('video')) {
      return BulbBusyCoverService.showBusyDuring(
        BulbAssetLibraryService.api.createVideo(folderId, entry).then((res) => {
          data.entry = res.entry;
        }),
      );
    } if (assetType.includes('audio')) {
      return BulbBusyCoverService.showBusyDuring(
        BulbAssetLibraryService.api.createAudio(folderId, entry).then((res) => {
          data.entry = res.entry;
        }),
      );
    }
    // Everything else is a file type :
    return BulbBusyCoverService.showBusyDuring(
      BulbAssetLibraryService.api.createAttachedFile(folderId, entry).then((res) => {
        data.entry = res.entry;
      }),
    );
  }

  /**
   * Helper method to generate the Uppy Upload Options.
   *
   * @param allowedTypes
   * @private
   */
  generateUppyUploadOptions(allowedTypes : String) : any {
    // Add the allowed types to Uppy's restrictions object :
    // Uppy signifies 'any' file type as having no 'allowedFileTypes' array in the restrictions object :
    if (allowedTypes !== '*') {
      return merge(
        {},
        this.defaultUppyOptions,
        {
          restrictions: {
            allowedFileTypes: allowedTypes.split(','),
            maxNumberOfFiles: 1,
          },
        },
      );
    }
    return this.defaultUppyOptions;
  }
}
